import React, { useEffect, useMemo, useRef, useState } from 'react';
import { UserDetails, AvailabilityObject } from '../../types/schemas/abc';
import { getFirstName } from '../../utils/name';
import {
  TutorImage,
  TutorInfo,
  TutorCredentials,
  TagWrap,
  TutorName,
  Tag,
  TutorDetailWrapper2,
  BannerIconButton,
  PrimaryButton,
  SecondaryButton,
  ScheduleGrid,
  GridHeaderCell,
  TimeHeaderCell,
  ScheduleCell,
  ScheduleKeyContainer,
  ScheduleKeyItem,
  ScheduleKeyColorBox,
  ScheduleKeyLabel,
  PriceDisplay,
  CornerTag,
  DetailContentArea,
  DetailFooter,
  DetailPageShell3,
  DetailCardHeader,
  DetailContent,
  ButtonText,
} from '../../styles/search/search.style';
import {
  DescriptionText,
  GridHeader,
  GridSubjectLabel,
  GridTickCell,
  KeyIconWrapper,
  SubheaderContainer,
  SubheaderLine,
  SubheaderTitle,
  SubjectsGrid,
} from '../../styles/profile/form.style';
import {
  CheckLg,
  XCircleFill,
  Search,
  ChatLeftText,
  X,
} from 'react-bootstrap-icons';
import QualificationsDisplay from '../profile/QualificationDisplay';
import { useConnections } from '../../hooks/useConnections';
import { useNavigate } from 'react-router-dom';
import { calculateFinalCost } from '../../utils/pricing';
import { useSubjectGrid } from '../../hooks/useSubjectGrid';
import { useAvailability } from '../../hooks/useAvailability';
import { getTimezoneOffset, adjustAvailabilityPerDay, GRANULARITY, DAYS_MONDAY_FIRST } from '../../utils/availability';
import { getDetectedTimezone, shortTzName, friendlyTzName } from '../../utils/timezones';
import { Globe } from 'lucide-react';

interface TutorDetailViewProps {
  tutor: UserDetails;
  parent: UserDetails;
  onClose: () => void;
  showActions?: boolean;
}

export const TutorDetailView: React.FC<TutorDetailViewProps> = ({
  tutor,
  parent,
  onClose,
  showActions = true,
}) => {


  const { createParentConnection } = useConnections();
  const navigate = useNavigate(); // Initialize useNavigate
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const tutorExpertiseMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    if (tutor?.expertise) {
      for (const [subject, levels] of Object.entries(tutor.expertise)) {
        map.set(subject, new Set(levels));
      }
    }
    return map; 

  }, [tutor?.expertise]) 

  const parentExpertiseMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    if (parent?.expertise) {
      for (const [subject, levels] of Object.entries(parent.expertise)) {
        map.set(subject, new Set(levels))
      }
    }
    return map;

  }, [parent?.expertise]);

  const { 
    subjects: parentSubjects, 
    sortedLevels: parentLevels, 
    gridColumns 
  } = useSubjectGrid(parent?.expertise)

  const browserTz = useMemo(() => getDetectedTimezone(), []);
  const homeTz = parent?.timezone || 'Europe/London';
  const tutorTz = tutor?.timezone;

  const tzEntries = useMemo(() => {
    const tzMap = new Map<string, { key: string; parts: string[] }>();
    const homeEntry = tzMap.get(homeTz) || { key: 'home', parts: [] };
    homeEntry.parts.push('Home');
    tzMap.set(homeTz, homeEntry);
    const browserEntry = tzMap.get(browserTz) || { key: 'browser', parts: [] };
    browserEntry.parts.push('Current');
    tzMap.set(browserTz, browserEntry);
    if (tutorTz) {
      const tutorEntry = tzMap.get(tutorTz) || { key: 'tutor', parts: [] };
      tutorEntry.parts.push("Tutor's");
      tzMap.set(tutorTz, tutorEntry);
    }
    return Array.from(tzMap.entries()).map(([tz, { key, parts }]) => ({
      key, tz, label: `${parts.join(' / ')} timezone (${friendlyTzName(tz)})`,
    }));
  }, [homeTz, browserTz, tutorTz]);

  const [activeTzKey, setActiveTzKey] = useState('home');
  const activeTz = tzEntries.find(e => e.key === activeTzKey)?.tz || homeTz;
  const tzSwitchButtons = tzEntries.filter(e => e.key !== activeTzKey);

  const buildDayOffsets = (fromTz: string, toTz: string): number[] => {
    const now = new Date();
    const todayMon = (now.getDay() + 6) % 7;
    return DAYS_MONDAY_FIRST.map((_, i) => {
      const d = new Date(now);
      d.setDate(d.getDate() + (i - todayMon));
      return getTimezoneOffset(toTz, fromTz, d);
    });
  };

  const parentAvailInActiveTz = useMemo(() => {
    if (!parent?.availability || activeTz === homeTz) return parent?.availability;
    return adjustAvailabilityPerDay(parent.availability, buildDayOffsets(homeTz, activeTz!), DAYS_MONDAY_FIRST);
  }, [parent?.availability, activeTz, homeTz]);

  const tutorAvailInActiveTz = useMemo(() => {
    if (!tutor?.availability || !tutorTz || activeTz === tutorTz) return tutor?.availability;
    return adjustAvailabilityPerDay(tutor.availability, buildDayOffsets(tutorTz, activeTz!), DAYS_MONDAY_FIRST);
  }, [tutor?.availability, tutorTz, activeTz]);

  const { availabilityStatusMap, hoursOfDay } = useAvailability(
    { availability: parentAvailInActiveTz, timezone: activeTz },
    { availability: tutorAvailInActiveTz, timezone: activeTz }
  );

  const timezoneNote = useMemo(() => {
    if (!activeTz || !tutor?.timezone) return null;
    if (activeTz === tutor.timezone) return `Availability overlap shown in ${friendlyTzName(activeTz)}.`;
    return `Tutor's timezone: ${friendlyTzName(tutor.timezone)}. Shown in your time (${friendlyTzName(activeTz)}).`;
  }, [activeTz, tutor?.timezone]);

  const parentRequiredSubjectsSet = useMemo(() => {
    if (!parent?.subjects) return new Set<string>();

    return new Set(parent.subjects);
  }, [parent?.subjects]);

  // This targets your specific container div instead of the whole window
  useEffect(() => {
    scrollContainerRef.current?.scrollTo(0, 0);
  }, []); // The empty dependency array ensures this runs only once when the component mounts

  const daysOfWeek = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];

  // This now uses the new sortedLevels to build the column widths dynamically.

  const credentials = useMemo(() => {
    const parts = [];
    if (tutor.degreeCourse) parts.push(tutor.degreeCourse);
    // if (tutor.university) parts.push(`at ${tutor.university}`);

    const mainCredentials = parts.join(' ');

    if (tutor.classification) {
      return `${mainCredentials} • ${tutor.classification}`;
    }
    return mainCredentials;
  }, [tutor.degreeCourse, tutor.university, tutor.classification]);

  const handleSaveAndSearch = async () => {
    try {
      // The status 'saved' is for when the user just wants to save and keep looking
      await createParentConnection({ tutorId: tutor.id, status: 'saved' });
      onClose(); // Close the modal on success
    } catch (error) {
      console.error('Failed to save connection:', error);
    }
  };

  const handleSaveAndMessage = async () => {
    try {
      // The status 'contacted' indicates the parent wants to start a conversation
      const newConnection = await createParentConnection({
        tutorId: tutor.id,
        status: 'contacted',
      });
      if (newConnection) {
        navigate(`/messenger?open=${newConnection.data.id}`);
      }
      onClose();
    } catch (error) {
      console.error('Failed to save and message:', error);
    }
  };

  const finalPrice = useMemo(() => {
    if (!tutor.hourlyRate) return null;
    return calculateFinalCost(tutor.hourlyRate);
  }, [tutor.hourlyRate]);

  const promoTags = useMemo(() => {
    const tags = [];
    if (tutor.offersFreeFirstLesson) {
      tags.push({ text: '1st Lesson Free', color: 'green' as const });
    }
    if (tutor.offersFreeIntroCall) {
      tags.push({ text: 'Free Intro Call', color: 'blue' as const });
    }
    return tags; // Returns an array, which could be empty or have one or two tags
  }, [tutor.offersFreeFirstLesson, tutor.offersFreeIntroCall]);

  return (
    <DetailPageShell3>
      <DetailCardHeader>
        {getFirstName(tutor?.name)}
        {tutor?.university && `(${tutor.university})`}
        <BannerIconButton
          onClick={() => {
            onClose(); // This is already functional and closes the view
          }}
        >
          <XCircleFill size={18} />
        </BannerIconButton>
      </DetailCardHeader>

      <DetailContent ref={scrollContainerRef}>
        <TutorDetailWrapper2>
          <TutorImage
            src={tutor.profilePhotoUrl || '/default-avatar.png'}
            alt={getFirstName(tutor.name) || 'Tutor'}
          />
          <TutorInfo isDetailedView={true}>
            <TutorName>
              {getFirstName(tutor.name) || 'Tutor'}
              {tutor?.university && ` (${tutor.university})`}
            </TutorName>
            {credentials && <TutorCredentials>{credentials}</TutorCredentials>}

            {tutor.subjects && tutor.subjects.length > 0 && (
              <TagWrap detailedView={true}>
                {tutor.subjects.map((subject) => {
                  const isMatch = parentRequiredSubjectsSet.has(subject);
                  return (
                    <Tag key={subject} isMatch={isMatch}>
                      {subject}
                    </Tag>
                  );
                })}
              </TagWrap>
            )}
            <PriceDisplay css={{ marginTop: '1rem' }}>
              {`£${finalPrice}/hr`}
            </PriceDisplay>

            <TagWrap css={{ marginTop: '0.75rem', justifyContent: 'center' }}>
              {promoTags.map((tag) => (
                <CornerTag key={tag.text} color={tag.color}>
                  {tag.text}
                </CornerTag>
              ))}
            </TagWrap>
          </TutorInfo>
        </TutorDetailWrapper2>

        <SubheaderContainer>
          <SubheaderLine />
          <SubheaderTitle>Description</SubheaderTitle>
          <SubheaderLine />
        </SubheaderContainer>

        <DescriptionText
          // Conditionally add a class if the description doesn't exist
          className={!tutor?.description ? 'not-provided' : ''}
        >
          {tutor?.description || 'No description provided yet.'}
        </DescriptionText>

        <SubheaderContainer>
          <SubheaderLine />
          <SubheaderTitle>Subjects Taught</SubheaderTitle>
          <SubheaderLine />
        </SubheaderContainer>

        {parentSubjects.length > 0 && (
          <ScheduleKeyContainer>
            <ScheduleKeyItem>
              <KeyIconWrapper color="match">
                <CheckLg size={14} />
              </KeyIconWrapper>
              <ScheduleKeyLabel>Exact match</ScheduleKeyLabel>
            </ScheduleKeyItem>
            <ScheduleKeyItem>
              <KeyIconWrapper color="partial">
                <CheckLg size={14} />
              </KeyIconWrapper>
              <ScheduleKeyLabel>Teaches subject, different level</ScheduleKeyLabel>
            </ScheduleKeyItem>
            <ScheduleKeyItem>
              <KeyIconWrapper color="notTaught">
                <X size={14} />
              </KeyIconWrapper>
              <ScheduleKeyLabel>Not covered</ScheduleKeyLabel>
            </ScheduleKeyItem>
          </ScheduleKeyContainer>
        )}

        {parentSubjects.length > 0 ? (
            <>
              <SubjectsGrid
                css={{
                  display: 'grid',
                  gridTemplateColumns: gridColumns,
                  overflowX: 'auto',
                }}
              >
                {/* 1. Header Row */}
                <GridHeader />
                {parentLevels.map(({ level }) => (
                  <GridHeader key={level}>{level}</GridHeader>
                ))}

                {parentSubjects.map((subject) => {
                  const requiredLevels = parentExpertiseMap.get(subject) || new Set();

                  return (
                    <React.Fragment key={subject}>
                      <GridSubjectLabel>{subject}</GridSubjectLabel>
                      {parentLevels.map(({ level }) => {
                        const tutorHasIt = tutorExpertiseMap.get(subject)?.has(level);
                        const parentNeedsIt = requiredLevels.has(level);

                        // Case 1: Tutor has it AND Parent needs it -> Exact Match
                        if (tutorHasIt && parentNeedsIt) {
                          return (
                            <GridTickCell key={level} status="exact">
                              <CheckLg size={18} />
                            </GridTickCell>
                          );
                        }

                        // Case 2: Tutor has it, but Parent didn't ask for it -> Partial/Available
                        if (tutorHasIt) {
                          return (
                            <GridTickCell key={level} status="partial">
                               <CheckLg size={18} />
                            </GridTickCell>
                          );
                        }

                        if (parentNeedsIt && !tutorHasIt) {
                          return (
                            <GridTickCell key={level} status="notTaught">
                               <X size={18} />
                            </GridTickCell>
                          );
                        }


                        // Case 3: Tutor does not have it -> Empty
                        return (
                          <GridTickCell key={level} status="default">
                          </GridTickCell>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </SubjectsGrid>
          </>
        ) : (
          <DescriptionText className={'not-provided'}>
            No subjects provided yet.
          </DescriptionText>
        )}

        <SubheaderContainer>
          <SubheaderLine />
          <SubheaderTitle>Qualifications</SubheaderTitle>
          <SubheaderLine />
        </SubheaderContainer>

        <QualificationsDisplay groups={tutor?.qualificationGroups ?? []} />

        <SubheaderContainer>
          <SubheaderLine />
          <SubheaderTitle>Weekly Schedule</SubheaderTitle>
          <SubheaderLine />
        </SubheaderContainer>

        {tzSwitchButtons.length > 0 && (
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
            {tzSwitchButtons.map(btn => (
              <button
                key={btn.key}
                onClick={() => setActiveTzKey(btn.key)}
                style={{
                  all: 'unset', display: 'flex', alignItems: 'center', gap: '0.35rem',
                  padding: '0.3rem 0.65rem', borderRadius: '0.5rem', fontSize: '0.75rem', fontWeight: 500,
                  cursor: 'pointer', whiteSpace: 'nowrap', border: '1px solid var(--colors-sand7, #d4d4d4)',
                  color: 'var(--colors-sand12, #1a1a1a)', transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--colors-sand4, #f0f0f0)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <Globe size={13} />
                View in {btn.label}
              </button>
            ))}
          </div>
        )}

        {availabilityStatusMap.size > 0 && (
          <ScheduleKeyContainer>
            <ScheduleKeyItem>
              <ScheduleKeyColorBox color="user1Only" />
              <ScheduleKeyLabel>Your Availability</ScheduleKeyLabel>
            </ScheduleKeyItem>
            <ScheduleKeyItem>
              <ScheduleKeyColorBox color="user2Only" />
              <ScheduleKeyLabel>Tutor's Availability</ScheduleKeyLabel>
            </ScheduleKeyItem>
            <ScheduleKeyItem>
              <ScheduleKeyColorBox color="overlap" />
              <ScheduleKeyLabel>Overlap</ScheduleKeyLabel>
            </ScheduleKeyItem>
          </ScheduleKeyContainer>
        )}

        {availabilityStatusMap.size > 0 ? (
          <ScheduleGrid css={{ overflowX: 'auto' }}>
            <GridHeaderCell />
            {daysOfWeek.map((day) => (
              <GridHeaderCell key={day}>{day}</GridHeaderCell>
            ))}

            {hoursOfDay.map((minute) => (
              <React.Fragment key={minute}>
                <TimeHeaderCell>
                  {new Date(0, 0, 0, 0, minute).toLocaleTimeString([], {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                  })}
                </TimeHeaderCell>

                {daysOfWeek.map((day) => {
                  const key = `${day.toLowerCase()}-${minute}`;
                  const status = availabilityStatusMap.get(key) || 'empty';

                  return <ScheduleCell key={key} status={status as any} />;
                })}
              </React.Fragment>
            ))}
          </ScheduleGrid>
        ) : (
          <DescriptionText className={'not-provided'}>
            No availability information found for this tutor or yourself.
          </DescriptionText>
        )}
        {timezoneNote && availabilityStatusMap.size > 0 && (
          <p style={{ margin: '0.6rem 0 0', fontSize: '0.75rem', color: '#065f46', fontStyle: 'italic' }}>
            {timezoneNote}
          </p>
        )}
      </DetailContent>

      <DetailFooter>
        {showActions && (
          <>
            <SecondaryButton
              css={{ flex: '0 1 auto', minWidth: 0, marginRight: '0.5rem' }}
              onClick={() => {
                handleSaveAndSearch();
                onClose();
              }}
            >
              <ButtonText>Save & Continue Search</ButtonText>
              <Search size={16} />
            </SecondaryButton>

            <PrimaryButton
              css={{ flex: '0 1 auto', minWidth: 0, marginLeft: '0.5rem' }}
              onClick={() => {
                handleSaveAndMessage();
              }}
            >
              <ButtonText css={{ color: 'white' }}>Save & Message</ButtonText>
              <ChatLeftText color="white" size={16} />
            </PrimaryButton>
          </>
        )}
      </DetailFooter>
    </DetailPageShell3>
  );
};